import { squareMetersToSquareFeet } from 'src/lib/conversionUtils';
import constants from 'src/constants';

const { MAIN_MATERIALS } = constants.measure;

/**
 * Class representing the main materials of a building.
 * Check the constants file for a list of the main materials.
 * The constructor depends on data which is generated by a Hover.Building object.
 * Documentation of Hover.Building objects can be found here: https://github.com/hoverinc/machete
 */
export default class MainMaterials {
  /**
   * @param {Object.<string, Object>} labelAreas areas for each labelId in meters
   * @param {Array.<Hover.ModelElement>} elements facades and annotations of a building
   */
  constructor(labelAreas, elements) {
    this.areaType = 'facadesMinusAnnotations'; /** @const */
    this.charCodeA = 65; /** @const */
    this.numLettersInAlphabet = 26; /** @const */
    this.labelAreas = labelAreas;
    this.elements = elements;
    this.labelIds = this.extractMainMaterialsLabelIds();
    this.elementsByLabelId = this.getElementsByLabelId();
  }

  /**
   * @returns {Array.number} labelIds that are main materials
   * @private
   */
  extractMainMaterialsLabelIds() {
    return Object.keys(this.labelAreas)
      .filter((labelId) => MAIN_MATERIALS[labelId])
      .map(Number);
  }

  /**
   * @returns {Object.<string, Array>} empty arrays for each labelId
   * @private
   */
  initElementsByLabelId() {
    return this.labelIds.reduce((acc, labelId) => {
      acc[labelId] = [];
      return acc;
    }, {});
  }

  /**
   * @returns {Object.<string, Array>} elements for each labelId
   * @private
   */
  getElementsByLabelId() {
    return this.elements.reduce((acc, element) => {
      this.labelIds.forEach((labelId) => {
        if (element.containsLabel(labelId)) {
          acc[labelId].push(element);
        }
      });
      return acc;
    }, this.initElementsByLabelId());
  }

  /**
   * @param {number} index between 0 and 25
   * @returns {string} uppercase letter (e.g. 'Z')
   * @private
   */
  convertIndexToLetter(index) {
    if (index < 0 || index > 25) {
      throw new Error('Index is out of bounds');
    }
    return String.fromCharCode(index + this.charCodeA);
  }

  /**
   * @param {number} index minimum of 0
   * @returns {string} uppercase letter(s) (e.g. 'C' or 'AB')
   * @private
   */
  convertIndexToKey(index) {
    if (index < 0) throw new Error('Index is out of bounds');
    if (index >= this.numLettersInAlphabet) {
      const letter = this.convertIndexToLetter(
        index % this.numLettersInAlphabet,
      );
      const nextIndex = Math.floor(index / this.numLettersInAlphabet) - 1;
      return this.convertIndexToKey(nextIndex) + letter;
    }
    return this.convertIndexToLetter(index);
  }

  /**
   * @param {Object} element
   * @param {number} index
   * @returns {Object.<string, number>}
   * @returns {string} element.key - SI-1, SI-2, SI-3 or A, B, C (if legacy)
   * @returns {number} element.sortIndex - 0, 1, 2, 3, etc
   * @private
   */
  getKeyAndSortIndex(element, index) {
    let key;
    let sortIndex;

    if (element?.manowarData?.name) {
      // use keys that are consistent with PDF (SI-1, SI-2, SI-3, etc)
      key = element.manowarData.name;
      sortIndex = this.extractNumberFromKey(key);
    } else {
      // use legacy keys that are not consistent with PDF (A, B, C, etc)
      key = this.convertIndexToKey(index);
      sortIndex = index;
    }

    return {
      key,
      sortIndex,
    };
  }

  /**
   * @param {string} key - SI-1, SI-2, SI-3, etc
   * @returns {number}
   * @private
   */
  extractNumberFromKey(key) {
    const matchArray = key.match(/\d+$/);
    if (matchArray === null) return 0;
    return Number(matchArray[0]);
  }

  /**
   * @param {(string|number)} labelId
   * @returns {Array.Object} elements containing the labelId
   * @private
   */
  getElementItems(labelId) {
    return this.elementsByLabelId[labelId]
      .map((element, index) => {
        const { key, sortIndex } = this.getKeyAndSortIndex(element, index);
        return {
          key,
          sortIndex,
          uuid: element.uuid,
          area: {
            meters: element.area,
            feet: squareMetersToSquareFeet(element.area),
          },
        };
      })
      .sort((a, b) => a.sortIndex - b.sortIndex);
  }

  /**
   * @param {(string|number)} labelId
   * @returns {{meters: number, feet: number}}
   * @private
   * In order to reduce customer confusion, total areas should equal the sum
   * of items as displayed in the app (i.e. a the sum of rounded numbers). All other
   * measurement products will also follow this rule. See Jira TDP-1080 for more information:
   * https://hoverinc.atlassian.net/browse/TDP-1080
   */
  getTotalAreaFromRoundedItems(labelId) {
    const items = this.getElementItems(labelId);
    return items.reduce(
      (acc, item) => {
        return {
          meters: acc.meters + Math.round(item.area.meters),
          feet: acc.feet + Math.round(item.area.feet),
        };
      },
      { meters: 0, feet: 0 },
    );
  }

  /**
   * @param {(string|number)} labelId
   * @returns {number}
   * @private
   */
  getCount(labelId) {
    return this.elementsByLabelId[labelId].length;
  }

  /**
   * @param {(string|number)} labelId
   * @returns {Object}
   * @private
   */
  getTotals(labelId) {
    return {
      area: this.getTotalAreaFromRoundedItems(labelId),
      count: this.getCount(labelId),
    };
  }

  /**
   * This is the only method in the class which is public.
   * It builds and formats the main materials data.
   * @returns {Object.<string, Object>}
   * @public
   */
  getData() {
    return this.labelIds.reduce((acc, labelId) => {
      const mainMaterialName = MAIN_MATERIALS[labelId];
      const items = this.getElementItems(labelId);
      const total = this.getTotals(labelId);

      acc[mainMaterialName] = { items, total };
      return acc;
    }, {});
  }
}
